'use client';

import { createContext, useContext, ReactNode } from 'react';

interface AdSlotData {
  desktopAdCode: string | null;
  mobileAdCode: string | null;
  isMultiple?: boolean;
  slots?: Array<{ desktopAdCode: string | null; mobileAdCode: string | null }>;
  totalSlots?: number;
  [key: string]: any;
}

interface AdDataContextType {
  adSlots: Record<string, AdSlotData>;
  websiteSettings: Record<string, any>;
  layoutCodes: Record<string, any>;
  routePageIds: Record<string, string>; // pathname → page_id mapping
}

const AdDataContext = createContext<AdDataContextType>({
  adSlots: {},
  websiteSettings: {},
  layoutCodes: {},
  routePageIds: {},
});

interface AdDataProviderProps {
  adSlots: Record<string, AdSlotData>;
  websiteSettings: Record<string, any>;
  layoutCodes?: Record<string, any>;
  routePageIds?: Record<string, string>;
  children: ReactNode;
}

export function AdDataProvider({ adSlots, websiteSettings, layoutCodes = {}, routePageIds = {}, children }: AdDataProviderProps) {
  return (
    <AdDataContext.Provider value={{ adSlots, websiteSettings, layoutCodes, routePageIds }}>
      {children}
    </AdDataContext.Provider>
  );
}

export function useAdSlot(slug: string): AdSlotData | null {
  return useContext(AdDataContext).adSlots[slug] || null;
}

export function useAllAdSlots(): Record<string, AdSlotData> {
  return useContext(AdDataContext).adSlots;
}

export function useWebsiteSetting(slug: string): any | null {
  return useContext(AdDataContext).websiteSettings[slug] || null;
}

export function useLayoutCodes(): Record<string, any> {
  return useContext(AdDataContext).layoutCodes;
}

export function useRoutePageIds(): Record<string, string> {
  return useContext(AdDataContext).routePageIds;
}

export function useCurrentPageId(): string | null {
  // This is now derived client-side from pathname + routePageIds
  // Kept for backward compatibility but prefer useRoutePageIds + pathname
  return null;
}
